import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";

const supplyPresets = [
  {
    value: "21000000",
    label: "21 Million",
    description: "(Like Bitcoin)"
  },
  {
    value: "1000000",
    label: "1 Million",
    description: "(Common for small to medium projects)"
  },
  {
    value: "100000000",
    label: "100 Million",
    description: "(Popular choice, similar to BNB's initial supply)"
  },
  {
    value: "1000000000",
    label: "1 Billion",
    description: "(Large supply, similar to many meme tokens)"
  },
  {
    value: "1000000000000",
    label: "1 Trillion",
    description: "(Very large supply, like SHIB's initial supply)"
  },
  {
    value: "custom",
    label: "Custom Amount",
    description: "(Enter your own supply amount)"
  }
];

export function TokenSupplyField() {
  const { register, setValue, watch, control, formState: { errors } } = useFormContext();
  const totalSupply = watch("totalSupply");
  const totalSupplyPreset = watch("totalSupplyPreset");
  const [selectedPreset, setSelectedPreset] = useState(totalSupplyPreset || "custom");
  const customAmount = watch("customSupply");

  // Watch for changes to totalSupply and update the preset accordingly
  useEffect(() => {
    // Only update preset if we're not already in custom mode
    if (selectedPreset !== "custom") {
      const preset = supplyPresets.find(p => p.value === totalSupply);
      if (preset) {
        setSelectedPreset(preset.value);
      } else if (totalSupply) {
        setSelectedPreset("custom");
        setValue("customSupply", totalSupply);
      }
    }
  }, [totalSupply, setValue, selectedPreset]);

  // Initialize with totalSupplyPreset if available
  useEffect(() => {
    if (totalSupplyPreset) {
      setSelectedPreset(totalSupplyPreset);
      if (totalSupplyPreset !== "custom") {
        setValue("totalSupply", totalSupplyPreset, { shouldValidate: true });
      }
    }
  }, [totalSupplyPreset, setValue]);

  const handlePresetChange = (value: string) => {
    setSelectedPreset(value);
    setValue("totalSupplyPreset", value, { shouldDirty: true });
    if (value !== "custom") {
      setValue("totalSupply", value, { shouldDirty: true, shouldValidate: true });
    } else {
      setValue("totalSupply", "", { shouldDirty: true, shouldValidate: true });
      setValue("customSupply", "");
    }
  };

  const handleCustomSupplyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("customSupply", value);
    setValue("totalSupply", value, { shouldValidate: true });
  };

  // Validate that the supply is greater than 0
  const validateSupply = (value: string) => {
    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue > 0;
  };

  return (
    <div className="space-y-2">
      <FormField
        control={control}
        name="totalSupply"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Total Supply</FormLabel>
            <FormControl>
              <div className="space-y-2">
                <Select
                  value={selectedPreset}
                  onValueChange={handlePresetChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supply amount" />
                  </SelectTrigger>
                  <SelectContent>
                    {supplyPresets.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        <div className="flex flex-col">
                          <span>{preset.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {preset.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedPreset === "custom" && (
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Enter custom supply amount"
                    {...register("customSupply")}
                    onChange={handleCustomSupplyChange}
                    className={errors.totalSupply ? "border-red-500" : ""}
                  />
                )}
              </div>
            </FormControl>
            <FormDescription>
              Choose the initial supply for your token
            </FormDescription>
            <FormMessage>
              {errors.totalSupply && (
                <span className="text-red-500">
                  {errors.totalSupply.message as string}
                </span>
              )}
            </FormMessage>
          </FormItem>
        )}
      />
    </div>
  );
}
